import { paths, RECORD_ORIGIN_SEARCH_PARAM, RECORD_PAGE_SEARCH_PARAM } from "~/config/routes";

import { composedTabKey, innerTabKey, resolveRecordPage } from "../pageModel/buildPageModel";
import { selectionParamName } from "../pageModel/selectionParams";
import { TAB_SLOT_PARAM_PREFIX } from "../pageModel/TabPathContext";
import type { NavBindPathHop, NavPageRecord, NavPageSlotRecord } from "../pageModel/types";
import type { SchemaRef } from "../utils/schemaOwner";

/** One step of a home list's path, from the record that holds the list down to the row. */
export interface RecordHomeHop {
  parent: SchemaRef;
  child: SchemaRef;
}

/**
 * A list on a record page that shows records of one type, scoped to the open record. It is the
 * type's home when the open record owns the row at every hop, or when the list is marked.
 */
export interface RecordHomeCandidate {
  slotKey: string;
  isRecordHome: boolean;
  /** The record page the list sits on, directly or through compose tabs. */
  recordPageKey: string;
  /** Whether that page is the one the root schema opens on anyway; else `?detailPage=` names it. */
  isDefaultPage: boolean;
  root: SchemaRef;
  /** `?tab=` on the root record page. Unset when the list sits on the Overview. */
  tabKey?: string;
  tabLabel?: string;
  /** `t.<composeSlot>` picks for the inner tab strips on the way down. */
  innerTabs: Record<string, string>;
  hops: RecordHomeHop[];
  /** The list's path walked back from the row to the root record, every hop incoming. */
  parentWalk: NavBindPathHop[];
}

interface HostPlacement {
  page: NavPageRecord;
  /** The slot sitting directly on the record page: the list itself or the compose tab around it. */
  entry: NavPageSlotRecord;
  innerTabs: Record<string, string>;
}

const versionOf = (version: string | undefined): string => version ?? "1.0.0";

const sameSchema = (left: SchemaRef, right: SchemaRef): boolean =>
  left.schema === right.schema && left.version === right.version;

/** Outgoing hops from the bound record to the row, or undefined for any other binding. */
const outgoingPathOf = (slot: NavPageSlotRecord): NavBindPathHop[] | undefined => {
  const target = slot.targetSchema;
  if (!target) {
    return undefined;
  }

  const path =
    slot.bindPath ??
    (slot.bindRelationship === target
      ? [{ resource: target, version: versionOf(slot.targetVersion) }]
      : undefined);
  if (!path || path.length === 0 || path.some((hop) => hop.direction === "incoming")) {
    return undefined;
  }

  return path[path.length - 1]?.resource === target ? path : undefined;
};

/**
 * The record page a slot ends up on. A landing page counts only when exactly one compose slot
 * embeds it, so the walk up is never a guess.
 */
const placeOnRecordPage = (
  slot: NavPageSlotRecord,
  pageByKey: ReadonlyMap<string, NavPageRecord>,
  slots: readonly NavPageSlotRecord[],
): HostPlacement | undefined => {
  const innerTabs: Record<string, string> = {};
  const visited = new Set<string>();
  let entry = slot;
  let pageKey = slot.page;

  while (!visited.has(pageKey)) {
    visited.add(pageKey);
    const page = pageByKey.get(pageKey);
    if (!page) {
      return undefined;
    }

    if (page.template === "record") {
      return { page, entry, innerTabs };
    }

    const embeddedIn = pageKey;
    const hosts = slots.filter(
      (candidate) =>
        candidate.type === "compose" &&
        (candidate.childPage === embeddedIn || candidate.childPages?.includes(embeddedIn)),
    );
    const host = hosts.length === 1 ? hosts[0] : undefined;
    if (!host) {
      return undefined;
    }

    if (host.childPage !== embeddedIn) {
      innerTabs[`${TAB_SLOT_PARAM_PREFIX}${host.key}`] = innerTabKey(embeddedIn);
    }
    entry = host;
    pageKey = host.page;
  }

  return undefined;
};

/**
 * Every list that could be the home of `schema` records: a collection of that type on a record
 * page, scoped to the open record through outgoing hops only. A list on a page about the same
 * type is skipped, because following it would keep climbing from parent to parent.
 */
export const findRecordHomeCandidates = (
  pages: readonly NavPageRecord[],
  slots: readonly NavPageSlotRecord[],
  schema: string,
  version: string,
): RecordHomeCandidate[] => {
  const pageByKey = new Map(pages.map((page) => [page.key, page]));
  const candidates: RecordHomeCandidate[] = [];

  for (const slot of slots) {
    if (
      slot.type !== "collection" ||
      slot.targetSchema !== schema ||
      versionOf(slot.targetVersion) !== version ||
      slot.bindSource !== "page" ||
      slot.bindEdgeOn !== "parent" ||
      slot.enabled === false
    ) {
      continue;
    }

    const path = outgoingPathOf(slot);
    const placement = path ? placeOnRecordPage(slot, pageByKey, slots) : undefined;
    const rootSchema = placement?.page.targetSchema;
    if (!path || !placement || !rootSchema || rootSchema === schema) {
      continue;
    }

    const root = { schema: rootSchema, version: versionOf(placement.page.targetVersion) };
    const chain: SchemaRef[] = [
      root,
      ...path.map((hop) => ({ schema: hop.resource, version: hop.version })),
    ];
    const hops = chain.slice(1).map((child, index) => ({ parent: chain[index] ?? root, child }));
    const parentWalk = chain
      .slice(0, -1)
      .reverse()
      .map((ref) => ({
        resource: ref.schema,
        version: ref.version,
        direction: "incoming" as const,
      }));
    const { entry } = placement;
    const onTab = entry.region === "tab";
    const innerTabs = { ...placement.innerTabs };
    if (slot.page !== placement.page.key && slot.region === "tab") {
      innerTabs[`${TAB_SLOT_PARAM_PREFIX}${entry.key}`] = composedTabKey(slot);
    }

    candidates.push({
      slotKey: slot.key,
      isRecordHome: slot.isRecordHome === true,
      recordPageKey: placement.page.key,
      isDefaultPage:
        resolveRecordPage(pages, root.schema, root.version)?.key === placement.page.key,
      root,
      tabKey: onTab ? composedTabKey(entry) : undefined,
      tabLabel: onTab ? entry.label?.trim() || entry.slug || entry.key : undefined,
      innerTabs,
      hops,
      parentWalk,
    });
  }

  return candidates;
};

/** Candidates marked `isRecordHome`. One settles the home without any ownership check. */
const markedHomes = (candidates: readonly RecordHomeCandidate[]): RecordHomeCandidate[] =>
  candidates.filter((candidate) => candidate.isRecordHome);

/**
 * Schemas whose owners the choice needs: the child of every hop. Empty when no candidate exists
 * or a marked list already decides.
 */
export const ownerLookupsFor = (candidates: readonly RecordHomeCandidate[]): SchemaRef[] => {
  if (candidates.length === 0 || candidates.length === 1 || markedHomes(candidates).length > 0) {
    return [];
  }

  const seen = new Map<string, SchemaRef>();
  for (const { hops } of candidates) {
    for (const { child } of hops) {
      seen.set(`${child.schema}@${child.version}`, child);
    }
  }

  return [...seen.values()];
};

/**
 * The home list. One marked list wins. A single candidate wins without ownership reads. When several
 * candidates exist, exactly one must reach the row through owning relationships at every hop. Two
 * marked lists, or zero or several owning ones, mean the record keeps its own page.
 *
 * `ownersOf` returns the schemas that own a child, or undefined when that is not known.
 */
export const chooseRecordHome = (
  candidates: readonly RecordHomeCandidate[],
  ownersOf: (child: SchemaRef) => readonly SchemaRef[] | undefined,
): RecordHomeCandidate | undefined => {
  const marked = markedHomes(candidates);
  if (marked.length > 0) {
    return marked.length === 1 ? marked[0] : undefined;
  }

  if (candidates.length === 1) {
    return candidates[0];
  }

  const owning = candidates.filter((candidate) =>
    candidate.hops.every(({ parent, child }) =>
      (ownersOf(child) ?? []).some((owner) => sameSchema(owner, parent)),
    ),
  );

  return owning.length === 1 ? owning[0] : undefined;
};

/** The root record's page, on the home tab, with the record selected in the home list. */
export const recordHomeHref = (
  home: RecordHomeCandidate,
  parentId: string,
  recordId: string,
  from: string | undefined,
): string => {
  const params = new URLSearchParams();
  if (!home.isDefaultPage) {
    params.set(RECORD_PAGE_SEARCH_PARAM, home.recordPageKey);
  }
  if (home.tabKey) {
    params.set("tab", home.tabKey);
  }
  for (const [name, value] of Object.entries(home.innerTabs)) {
    params.set(name, value);
  }
  params.set(selectionParamName(home.slotKey), recordId);
  if (from) {
    params.set(RECORD_ORIGIN_SEARCH_PARAM, from);
  }

  return `${paths.resourceDetail(home.root.schema, home.root.version, parentId)}?${params.toString()}`;
};
